import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { useSystem } from '@contexts/SystemContext'
import type { LoginCredentials } from '@/types'

const LoginPage: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { login, user, isLoading } = useAuth()
  const { systemSettings } = useSystem()
  const navigate = useNavigate()

  // 獲取軟體名稱，如果還沒載入則使用預設值
  const softwareName = systemSettings?.softwareName || 'AnyTime'

  // Redirect if already logged in
  useEffect(() => {
    if (user && !isLoading) {
      navigate('/')
    }
  }, [user, isLoading, navigate])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!credentials.email || !credentials.password) {
      return
    }

    setIsSubmitting(true)
    try {
      await login(credentials)
      // Navigation will be handled by the useEffect above
    } catch (error) {
      console.error('Login failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-secondary-600 font-medium">載入中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="max-w-md w-full mx-4">
        <div className="card-premium p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gradient mb-2">{softwareName}</h1>
            <p className="text-secondary-600">線上計時工具</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                電子郵件
              </label>
              <input
                type="email"
                name="email"
                value={credentials.email}
                onChange={handleInputChange}
                className="input-primary"
                placeholder="請輸入您的電子郵件"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                密碼
              </label>
              <input
                type="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                className="input-primary"
                placeholder="請輸入您的密碼"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn-primary w-full"
              disabled={isSubmitting || !credentials.email || !credentials.password}
            >
              {isSubmitting ? '登入中...' : '登入'}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-secondary-600">
              你的時間正在創造有意義的價值
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
